import type { RefObject } from "react"
import { useCallback, useEffect } from "react"
import { useAtom } from "jotai"

import { PlayButton } from "./PlayButton"
import {
  currentTimeAtom,
  durationAtom,
  useIsBuffering,
  useIsPlaying,
} from "./state"

export type ArticleAudioPlayerProps = {
  url: string
  audioRef: RefObject<HTMLAudioElement>
}

export const ArticleAudioPlayer: React.FC<ArticleAudioPlayerProps> = ({
  url,
  audioRef,
}) => {
  const [isPlaying, setIsPlaying] = useIsPlaying()
  const [, setIsBuffering] = useIsBuffering()

  const [, setCurrentTime] = useAtom(currentTimeAtom)
  const [, setDuration] = useAtom(durationAtom)

  const triggerPlay = useCallback(async () => {
    const audio = audioRef.current

    if (!audio) return
    try {
      await audio.play()
    } catch (e) {
      audio.pause()
    }
  }, [audioRef])

  useEffect(() => {
    const player = audioRef.current

    if (!player) return

    if (isPlaying) {
      triggerPlay().catch(() => {})
    } else {
      player.pause()
    }
  }, [audioRef, isPlaying, triggerPlay])

  return (
    <section className="w-full bg-white dark:bg-primary-800">
      <PlayButton />

      <audio
        key={url}
        src={url}
        ref={audioRef}
        onPlaying={() => setIsPlaying(true)}
        onCanPlay={() => setIsBuffering(false)}
        onWaiting={() => setIsBuffering(true)}
        onTimeUpdate={(e) => {
          const { currentTime: time = 0, duration: audioDuration = 0 } =
            e.currentTarget
          setCurrentTime(time)
          setDuration(audioDuration)
        }}
        onAbort={() => {
          setIsBuffering(false)
          setIsPlaying(false)
        }}
      />
    </section>
  )
}

export * from "./ProgressBar"
export * from "./state"
